import React from "react";
import get from "lodash.get";
import { ScaleLoader } from "react-spinners";
import { firebaseConnect } from "react-redux-firebase";
import { connect } from "react-redux";
import * as modalActions from "../../utils/actions/modals";
import { withRouter, RouterProps } from "../../utils/withRouter";
import ICharity from "../../types/services/ICharity";
import IGlobalConfig from "../../types/IGlobalConfig";
import { ExportType } from "../components/exportModal";
import * as userActions from "../../utils/actions/user";
import IColumnName from "../../types/IColumnName";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import IThankYouInfo from "../../types/services/IThankYouInfo";
import { PersonalViewSparkType } from "../components/thankYouModal";
import {
  ensureAccessTokenWithRetry,
  getCachedAccessToken,
  isAuthBridgeInitialized,
  isUsableServiceAccessToken,
  resolveServiceAccessTokenFromState,
  clearSessionAccessToken,
  subscribeAccessToken,
} from "../../utils/firebaseToken";
import { isAdminDebugUiActive } from "../../utils/adminDebugUi";
import { applyLocalServiceUrls } from "../../utils/localServiceUrls";
import {
  forceAdminSessionRecovery,
  SERVICE_ACCESS_TOKEN_WAIT_MS,
  AUTH_READY_GRACE_MS,
} from "../../utils/staleAuthRecovery";

let globalAccessToken: string | null = null;

export interface ServiceCallerProps extends RouterProps {
  config?: IGlobalConfig;
  serviceAccessToken?: string;
  currentCharity?: ICharity;
  isLoggedIn: boolean;
  isSuperadmin?: boolean;
  isAdminDebugUi?: boolean;
  isDonationReader?: boolean;
}

export interface ModalProps {
  toggleSegmentManagerModal: (
    donorId: string,
    updateComponentFunction?: (index) => void
  ) => void;
  toggleExportModal: (
    exportType: ExportType,
    exportIdsArray?: string[],
    exportTotalIds?: string,
    exportFromDate?: number,
    exportToDate?: number,
    ignoreSelected?: boolean,
    donorId?: string,
    exportCharity?: string,
    isFlag?: boolean,
    donorIds?: Array<string>,
    segmentId?: string,
    filterBy?: string,
    summaryExport?: boolean
  ) => void;
  toggleMediaModal: (
    mediaBlobs?: Array<Blob | string>,
    onlySMS?: boolean,
    hideCustomThumbnail?: boolean,
    customThumbnailUpload?: boolean,
    existingPreviewUrl?: string,
    existingPreviewIsCustom?: boolean,
    postTitle?: string
  ) => void;
  toggleCustomThumbnailModal: (thumbnailBlob?: Blob) => void;
  clearMediaModalBlobs: () => void;
  resetCustomThumbnailModal: (
    thumbnailBlob?: Blob,
    showModal?: boolean
  ) => void;
  toggleAddDonorModal: (
    donorFlagId?: string,
    donorFlagDonors?: Array<string>
  ) => void;
  toggleColumnsModal: (
    changeColumnFunction?: (index) => void,
    columnNames?: Array<IColumnName>
  ) => void;
  toggleThankYouModal: (
    thankYouInfos: Array<IThankYouInfo>,
    modalType: PersonalViewSparkType,
    goAction: () => void,
    dismissAction: () => void,
    skipOkConfirmation: boolean
  ) => void;
  toggleEditGiveUrlModal: (
    charityId: string,
    charityName: string,
    charityCurrentGiveUrl: string
  ) => void;
  columnsModalChange: (columnNames: Array<IColumnName>) => void;
}

export interface UserCharitiesProps extends RouterProps {
  currentCharity?: ICharity;
  charities: Array<ICharity>;
  changeCurrentCharity: (charity: ICharity) => void;
  updateCharities: (charities: Array<ICharity>) => void;
}

const mapUserCharitiesStateToProps = state => {
  const { user } = state;
  return {
    charities: user.charities.sort((n1, n2) => {
      if (n1.name < n2.name) return -1;
      if (n1.name > n2.name) return 1;
      return 0;
    }),
    currentCharity: user.currentCharity,
  };
};

const mapUserCharitiesDispatchToProps = dispatch => ({
  changeCurrentCharity: (charity = null) => {
    dispatch(userActions.changeCurrentCharity(charity));
  },
  updateCharities: (charities = null) => {
    dispatch(userActions.updateCharities(charities));
  },
});

const mapStateToProps = state => {
  const { firebase, user } = state;
  const global = firebase.data.global;
  const stsToken = get(firebase, "auth.stsTokenManager.accessToken") as
    | string
    | undefined;
  const safeServiceToken = resolveServiceAccessTokenFromState(stsToken);
  if (safeServiceToken) {
    globalAccessToken = safeServiceToken;
  }

  return {
    config: applyLocalServiceUrls(global ? global.config : null),
    currentCharity: user.currentCharity,
    isLoggedIn: user.isLoggedIn,
    serviceAccessToken: safeServiceToken,
    isSuperadmin: user.isSuperadmin,
    isAdminDebugUi: isAdminDebugUiActive(user.isSuperadmin),
    isDonationReader: user.isDonationReader,
  };
};

const mapDispatchToProps = dispatch => ({
  toggleSegmentManagerModal: (
    donorId: string,
    updateComponentFunction?: (index) => void
  ) => {
    dispatch(
      modalActions.toggleSegmentManagerModal(donorId, updateComponentFunction)
    );
  },
  toggleMediaModal: (
    mediaBlobs?: Array<Blob | string>,
    onlySMS?: boolean,
    hideCustomThumbnail?: boolean,
    customThumbnailUpload?: boolean,
    existingPreviewUrl?: string,
    existingPreviewIsCustom?: boolean,
    postTitle?: string
  ) => {
    dispatch(
      modalActions.toggleMediaModal(
        mediaBlobs,
        onlySMS,
        hideCustomThumbnail,
        customThumbnailUpload,
        existingPreviewUrl,
        existingPreviewIsCustom,
        postTitle
      )
    );
  },
  toggleCustomThumbnailModal: (thumbnailBlob?: Blob) => {
    dispatch(modalActions.toggleCustomThumbnailModal(thumbnailBlob));
  },
  clearMediaModalBlobs: () => {
    dispatch(modalActions.clearMediaModalBlobs());
  },
  resetCustomThumbnailModal: (thumbnailBlob?: Blob, showModal?: boolean) => {
    dispatch(modalActions.resetCustomThumbnailModal(thumbnailBlob, showModal));
  },
  toggleColumnsModal: (
    changeColumnFunction?: (index) => void,
    columnNames?: Array<IColumnName>
  ) => {
    dispatch(
      modalActions.toggleColumnsModal(changeColumnFunction, columnNames)
    );
  },
  columnsModalChange: (columnNames: Array<IColumnName>) => {
    dispatch(modalActions.columnsModalChange(columnNames));
  },
  toggleThankYouModal: (
    thankYouInfos: Array<IThankYouInfo>,
    modalType: PersonalViewSparkType,
    goAction: () => void,
    dismissAction: () => void,
    skipOkConfirmation: boolean
  ) => {
    dispatch(
      modalActions.toggleThankYouModal(
        thankYouInfos,
        modalType,
        goAction,
        dismissAction,
        skipOkConfirmation
      )
    );
  },

  toggleEditGiveUrlModal: (
    charityId: string,
    charityName: string,
    charityCurrentGiveUrl: string
  ) => {
    dispatch(
      modalActions.toggleEditGiveUrlModal(
        charityId,
        charityName,
        charityCurrentGiveUrl
      )
    );
  },

  toggleExportModal: (
    exportType: ExportType,
    exportIdsArray?: [string],
    exportTotalIds?: string,
    exportFromDate?: number,
    exportToDate?: number,
    ignoreSelected?: boolean,
    donorId?: string,
    exportCharity?: string,
    isFlag?: boolean,
    donorIds?: Array<string>,
    segmentId?: string,
    filterBy?: string,
    summaryExport?: boolean
  ) => {
    dispatch(
      modalActions.toggleExportModal(
        exportType,
        exportIdsArray,
        exportTotalIds,
        exportFromDate,
        exportToDate,
        ignoreSelected,
        donorId,
        exportCharity,
        donorIds,
        isFlag,
        segmentId,
        filterBy,
        summaryExport
      )
    );
  },
  toggleAddDonorModal: (donorFlagId, donorFlagDonors) => {
    dispatch(modalActions.toggleAddDonorModal(donorFlagId, donorFlagDonors));
  },
  togglePostNotificationModal: post => {
    dispatch(modalActions.togglePostNotificationModal(post));
  },
});

export function serviceCaller<Props extends ServiceCallerProps>(
  Component: React.ComponentClass<Props>
) {
  return connect<Props>(mapStateToProps, null)(
    withRouter(withServiceAccessToken(Component))
  );
}

interface ServiceAccessTokenState {
  accessToken: string | null;
  isLoggedIn: boolean;
}

function withServiceAccessToken<Props extends ServiceCallerProps>(
  Component: React.ComponentType<Props>
) {
  class ServiceAccessToken extends React.Component<Props, ServiceAccessTokenState> {
    private unsubs: Array<() => void> = [];
    private stuckWatchInterval: number | null = null;
    private stuckWatchStartedAt = 0;
    private sessionRecoveryTriggered = false;

    constructor(props: Props) {
      super(props);
      this.state = {
        accessToken: getCachedAccessToken() || globalAccessToken,
        isLoggedIn: false,
      };
    }

    static getDerivedStateFromProps(
      props: ServiceCallerProps,
      state: ServiceAccessTokenState
    ) {
      if (props.isLoggedIn !== state.isLoggedIn) {
        if (!props.isLoggedIn) {
          clearSessionAccessToken();
          globalAccessToken = null;
          return {
            isLoggedIn: false,
            accessToken: null,
          };
        }
        const token =
          props.serviceAccessToken || getCachedAccessToken() || null;
        return {
          isLoggedIn: props.isLoggedIn,
          accessToken: token,
        };
      }
      if (
        props.isLoggedIn &&
        props.serviceAccessToken &&
        props.serviceAccessToken !== state.accessToken
      ) {
        return {
          ...state,
          accessToken: props.serviceAccessToken,
        };
      }
      return null;
    }

    pickMergedServiceAccessToken(): string | undefined {
      if (!firebase.auth().currentUser) {
        return undefined;
      }
      const candidates = [
        getCachedAccessToken(),
        this.state.accessToken,
        this.props.serviceAccessToken,
        globalAccessToken,
      ];
      for (const c of candidates) {
        if (isUsableServiceAccessToken(c)) {
          return (c as string).trim();
        }
      }
      return undefined;
    }

    clearStuckWatch = () => {
      if (this.stuckWatchInterval != null) {
        window.clearInterval(this.stuckWatchInterval);
        this.stuckWatchInterval = null;
      }
    };

    triggerSessionRecovery = () => {
      if (this.sessionRecoveryTriggered || !this.props.isLoggedIn) {
        return;
      }
      this.sessionRecoveryTriggered = true;
      this.clearStuckWatch();
      forceAdminSessionRecovery();
    };

    startStuckWatch = () => {
      if (this.stuckWatchInterval != null || !this.props.isLoggedIn) {
        return;
      }
      this.stuckWatchStartedAt = Date.now();
      this.stuckWatchInterval = window.setInterval(() => {
        if (!this.props.isLoggedIn || this.sessionRecoveryTriggered) {
          this.clearStuckWatch();
          return;
        }
        if (isUsableServiceAccessToken(this.pickMergedServiceAccessToken())) {
          this.clearStuckWatch();
          return;
        }
        const elapsed = Date.now() - this.stuckWatchStartedAt;
        const authReady =
          isAuthBridgeInitialized() || elapsed >= AUTH_READY_GRACE_MS;
        if (authReady && !firebase.auth().currentUser) {
          this.triggerSessionRecovery();
          return;
        }
        if (elapsed >= SERVICE_ACCESS_TOKEN_WAIT_MS) {
          this.triggerSessionRecovery();
        }
      }, 500);
    };

    tryResolveAccessToken = () => {
      if (!this.props.isLoggedIn || !firebase.auth().currentUser) {
        return;
      }
      const needsForce =
        !isUsableServiceAccessToken(getCachedAccessToken()) &&
        !isUsableServiceAccessToken(this.pickMergedServiceAccessToken());

      ensureAccessTokenWithRetry({ forceRefresh: needsForce, retries: 1 })
        .then(idToken => {
          if (!isUsableServiceAccessToken(idToken)) {
            return;
          }
          globalAccessToken = idToken;
          this.clearStuckWatch();
          this.setState({ accessToken: idToken });
        })
        .catch(() => {
          this.triggerSessionRecovery();
        });
    };

    componentDidMount() {
      if (this.props.isLoggedIn) {
        this.startStuckWatch();
        this.tryResolveAccessToken();
      }

      this.unsubs.push(
        subscribeAccessToken(token => {
          if (!this.props.isLoggedIn) {
            return;
          }
          if (!isUsableServiceAccessToken(token)) {
            return;
          }
          globalAccessToken = token;
          this.clearStuckWatch();
          this.setState({ accessToken: token });
        })
      );
    }

    componentDidUpdate(prevProps: Props) {
      if (prevProps.isLoggedIn && !this.props.isLoggedIn) {
        this.sessionRecoveryTriggered = false;
        this.clearStuckWatch();
        return;
      }
      if (!prevProps.isLoggedIn && this.props.isLoggedIn) {
        this.sessionRecoveryTriggered = false;
        this.startStuckWatch();
        this.tryResolveAccessToken();
        return;
      }
      if (
        this.props.isLoggedIn &&
        isUsableServiceAccessToken(this.pickMergedServiceAccessToken())
      ) {
        this.clearStuckWatch();
        return;
      }
      if (this.props.isLoggedIn && this.stuckWatchInterval == null) {
        this.startStuckWatch();
      }
    }

    componentWillUnmount(): void {
      this.clearStuckWatch();
      this.unsubs.forEach(fn => {
        try {
          fn();
        } catch (_) {}
      });
      this.unsubs = [];
    }

    render() {
      if (!this.props.isLoggedIn) {
        return <Component {...this.props} />;
      }

      const token = this.pickMergedServiceAccessToken();
      if (!isUsableServiceAccessToken(token)) {
        return (
          <div
            style={{
              padding: 48,
              textAlign: "center",
              minHeight: "40vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ScaleLoader color="#1f65b1" loading />
          </div>
        );
      }

      return <Component {...this.props} serviceAccessToken={token} />;
    }
  }

  return ServiceAccessToken;
}

export function withLoginRedirect<Props extends ServiceCallerProps>(
  Component: React.ComponentType<Props>
) {
  class LoginRedirect extends React.Component<Props, { isLoggedIn: boolean }> {
    constructor(props: Props) {
      super(props);
      this.state = {
        isLoggedIn: false,
      };
    }

    static getDerivedStateFromProps(props: ServiceCallerProps, state) {
      if (props.isLoggedIn !== state.isLoggedIn) {
        return {
          isLoggedIn: props.isLoggedIn,
        };
      }
      return null;
    }

    componentDidUpdate(
      _: Readonly<Props>,
      prevState: Readonly<{ isLoggedIn: boolean }>
    ): void {
      if (!prevState.isLoggedIn && !this.state.isLoggedIn) {
        this.redirectLogin();
      }
    }

    redirectLogin = () => {
      this.props.navigate("/");
    };

    render() {
      return <Component {...this.props} />;
    }
  }

  return LoginRedirect;
}

export function withSuperadminLoginRedirect<Props extends ServiceCallerProps>(
  Component: React.ComponentClass<Props>
) {
  interface StateLoggedIn {
    isLoggedIn: boolean;
  }

  class SuperadminLoginRedirect extends React.Component<Props, StateLoggedIn> {
    constructor(props) {
      super(props);
      this.state = {
        isLoggedIn: false,
      };
    }

    static getDerivedStateFromProps(props, state) {
      if (props.isLoggedIn !== state.isLoggedIn) {
        return {
          isLoggedIn: props.isLoggedIn,
        };
      }
      // Return null to indicate no change to state.
      return null;
    }

    componentDidUpdate(
      _: Readonly<Props>,
      prevState: Readonly<StateLoggedIn>
    ): void {
      if (!prevState.isLoggedIn && !this.state.isLoggedIn) {
        this.redirectLogin();
      }
    }

    redirectLogin = () => {
      this.props.navigate("/contacts");
    };

    render() {
      return <Component {...this.props} />;
    }
  }

  return SuperadminLoginRedirect;
}

export function withModals<Props extends ModalProps>(
  component: React.ComponentClass<Props>
) {
  return connect<Props>(null, mapDispatchToProps)(component);
}

export function connectUserCharities<Props extends UserCharitiesProps>(
  Component: React.ComponentType<Props>
) {
  return connect(
    mapUserCharitiesStateToProps,
    mapUserCharitiesDispatchToProps
  )(withRouter(Component));
}

export function withUserCharities<Props extends UserCharitiesProps>(
  Component: React.ComponentType<Props>
) {
  return firebaseConnect((_, store) => {
    const charityId = store.getState().user.currentCharity?.id;
    if (!charityId) {
      return [];
    }
    return [
      `${charityId}/info`,
      `${charityId}/config/disableGive`,
      `${charityId}/onboardingRequired`,
    ];
  })(connectUserCharities(Component));
}

export function subscribeToResizeChanges(WrappedComponent) {
  interface StateI {
    mobileMode: boolean;
  }

  class Enhancer extends React.Component<StateI, any> {
    constructor(props) {
      super(props);
      this.handleWindowSize = this.handleWindowSize.bind(this);
      this.state = {
        mobileMode:
          typeof window !== "undefined" ? window.innerWidth < 768 : false,
      };
    }

    componentDidMount() {
      if (window) {
        this.handleWindowSize();
        window.addEventListener("resize", this.handleWindowSize);
      }
    }

    componentWillUnmount() {
      if (window) {
        window.removeEventListener("resize", this.handleWindowSize);
      }
    }

    handleWindowSize = () => {
      if (window) {
        this.setState({
          mobileMode: window ? window.innerWidth < 768 : false,
        });
      }
    };

    render() {
      return (
        <WrappedComponent mobileMode={this.state.mobileMode} {...this.props} />
      );
    }
  }

  return (props: any) => <Enhancer {...props} />;
}
